import React, { useState } from 'react';
import { Check, Trash2, Plus, Calendar } from 'lucide-react';

/**
 * Reusable task list for "Noch zu erledigen" in contact cards.
 * tasks: array of { id, text, dueDate, done, todoRef, createdAt }
 * isEditMode: shows delete button + add button
 * compact: show max 3 tasks, rest scrollable; truncate text
 * onToggle(taskId): toggle done status
 * onDelete(taskId): delete task
 * onAdd(): open ZuErledigienModal
 */
export default function KontaktTaskList({ tasks = [], isEditMode = false, compact = false, onToggle, onDelete, onAdd }) {
  const [showAll, setShowAll] = useState(false);

  const sorted = [...tasks].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });

  const MAX_VISIBLE = 3;
  const visible = compact && !showAll ? sorted.slice(0, MAX_VISIBLE) : sorted;
  const remaining = sorted.length - MAX_VISIBLE;

  const formatDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className={`space-y-1 ${compact ? 'max-h-40 overflow-y-auto pr-1' : ''}`}>
      {sorted.length === 0 && !isEditMode && (
        <p className="text-xs text-slate-400 italic">Keine Aufgaben</p>
      )}

      {visible.map(task => (
        <div
          key={task.id}
          className={`flex items-center gap-2 p-1.5 rounded-lg border transition-all ${
            task.done ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-200'
          }`}
        >
          {/* Checkbox */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggle && onToggle(task.id); }}
            type="button"
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              task.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-500'
            }`}
          >
            {task.done && <Check className="w-2.5 h-2.5" />}
          </button>

          {/* Text + due date */}
          <div className="flex-1 min-w-0">
            <p className={`text-xs leading-tight whitespace-pre-wrap break-words ${task.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
              {task.text}
            </p>
            {task.dueDate && !compact && (
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                <Calendar className="w-2.5 h-2.5" />
                {formatDate(task.dueDate)}
              </p>
            )}
          </div>

          {/* Delete button (only in edit mode) — always visible, not hover-only */}
          {isEditMode && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete && onDelete(task.id); }}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
              title="Aufgabe löschen"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}

      {/* Show more / less toggle in compact mode */}
      {compact && remaining > 0 && !showAll && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
          className="text-xs text-slate-400 hover:text-emerald-600 w-full text-left px-1.5"
        >
          + {remaining} weitere
        </button>
      )}
      {compact && showAll && sorted.length > MAX_VISIBLE && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowAll(false); }}
          className="text-xs text-slate-400 hover:text-emerald-600 w-full text-left px-1.5"
        >
          Weniger anzeigen
        </button>
      )}

      {/* Add button in edit mode */}
      {isEditMode && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAdd && onAdd(); }}
          className="w-full py-1.5 border-2 border-dashed border-slate-200 rounded-lg text-xs text-slate-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex items-center justify-center gap-1.5 mt-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Aufgabe hinzufügen
        </button>
      )}
    </div>
  );
}
